import { LinearProgress, Stack } from "@mui/material";
import MDDataGrid from "components/MDDataGrid/MDDataGrid";
import MDTypography from "components/MDTypography";
import { debounce, isEmpty } from "lodash";
import MDBox from "components/MDBox";
import EmptyTableRowOverlay from "components/MDEmptyOverlay/EmptyTableRowOverlay";
import { useState, useMemo, useEffect } from "react";
import MDSnackbar from "components/MDSnackbar";
import { capitalizeFirstLetter } from "global/functions";
import { useDispatch, useSelector } from "react-redux";
import { getLprStatistics } from "store/slice/lprStatistics/lprStatisticsSlice";

const LprTable = ({ placeId, startDate, endDate, searchText, tz }) => {
  const dispatch = useDispatch();
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);
  const loading = useSelector((state) => state.lprStatistic.loading || false);
  const lprDataList = useSelector((state) => state.lprStatistic.lprData || []);
  const totalRecords = useSelector((state) => state.lprStatistic.total || 0);

  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification((prev) => ({ ...prev, show: false })),
  });

  const getLPrData = async (pageNo) => {
    const payload = {
      pageNo: (pageNo || 0) + 1,
      tz,
      placeId,
      search: searchText,
      startDate,
      endDate,
    };
    dispatch(getLprStatistics(payload));
  };

  const debouncedGetLPrData = useMemo(
    () => debounce(getLPrData, 1000),
    [getLPrData],
  );

  useEffect(() => {
    debouncedGetLPrData(page);
    return () => debouncedGetLPrData.cancel();
  }, [startDate, endDate, searchText, placeId, page]);

  const handlePaginationChange = (newModel) => {
    setPage(newModel.page);
    setPageSize(newModel.pageSize);
    getLPrData(newModel.page);
  };

  const columnsData = [
    {
      field: "referenceNumber",
      headerName: "Notice Number",
      width: 120,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography display="block" variant="caption">
          {row.referenceNumber || "-"}
        </MDTypography>
      ),
    },
    {
      field: "plate",
      headerName: "License Plate",
      width: 180,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {row?.plate || "-"}
        </MDTypography>
      ),
    },
    {
      field: "entryTime",
      headerName: "Entry Time",
      width: 220,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          color="text"
          sx={{ whiteSpace: "normal" }}
        >
          {row.entryTime}
        </MDTypography>
      ),
    },
    {
      field: "exitTime",
      headerName: "Exit Time",
      width: 220,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          color="text"
          sx={{ whiteSpace: "normal" }}
        >
          {row.exitTime}
        </MDTypography>
      ),
    },
    {
      field: "noticeIssued",
      headerName: "Notice Issued",
      width: 180,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {row?.noticeIssued ? "Yes" : "No"}
        </MDTypography>
      ),
    },
    {
      field: "noticeIssuedBy",
      headerName: "Notice Issued By",
      width: 120,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {capitalizeFirstLetter(row?.noticeIssuedBy) || "-"}
        </MDTypography>
      ),
    },
    {
      field: "violationReason",
      headerName: "Reason Of Violation",
      width: 160,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {row?.violationReason || "No Payment"}
        </MDTypography>
      ),
    },
    {
      field: "isParkPliantUpdateLP",
      headerName: "Updated by ParkPliant",
      width: 160,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {row?.isParkPliantUpdateLP ? "Yes" : "No"}
        </MDTypography>
      ),
    },
    {
      field: "updatedBy",
      headerName: "Updated By",
      width: 160,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {row?.updatedBy || "-"}
        </MDTypography>
      ),
    },
    {
      field: "correctedPlate",
      headerName: "Corrected Plate",
      width: 160,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {row?.correctedPlate || "-"}
        </MDTypography>
      ),
    },

    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: ({ row }) => (
        <Stack direction="row" alignItems="center" gap={2}></Stack>
      ),
    },
  ];

  return (
    <>
      <MDBox mt={2}>
        <MDDataGrid
          rows={
            Array.isArray(lprDataList) && lprDataList[0]?._id ? lprDataList : []
          }
          columns={columnsData}
          getRowId={(row) => row._id}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          loading={loading}
          pagination
          page={page}
          pageSize={pageSize}
          paginationMode="server"
          rowCount={totalRecords}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationChange}
          components={{
            NoRowsOverlay: EmptyTableRowOverlay,
            LoadingOverlay: LinearProgress,
          }}
          hideFooterPagination={isEmpty(lprDataList)}
        />
      </MDBox>

      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
    </>
  );
};

export default LprTable;
